import React, { useState, useEffect } from "react";
import {
    Paper,
    Box,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import SectionTitle from "../../common/SectionTitle";
import CurrentClientContext from "../../../providers/CurrentClientProvider";
import TipsContext from "../../../providers/GlobalTip";
import PosService from "../../../services/PosService";
import { trackEvent, handleTrackError } from "../../../services/AmplitudeService";
import constants from "../../../constants/constant";

function HeartlandSalesAttributionToggle() {
    const currentClient = CurrentClientContext.useClient();
    const tip = TipsContext.useMessage();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("fulfillment");

    const client = currentClient.client;
    const isHeartlandRetail =
        client && Number(client.PointOfSaleId) === constants.POS_ID.HEARTLAND_RETAIL_API;

    useEffect(() => {
        if (!isHeartlandRetail) return;
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const res = await PosService.getHeartlandSalesAttribution(client.Id);
                if (cancelled) return;
                setMode(res?.mode || "fulfillment");
            } catch (e) {
                if (cancelled) return;
                console.error(e);
                tip.show("Could not load sales attribution setting");
                handleTrackError({
                    error: e,
                    pageName: "Client POS Mapping",
                    additionalMetadata: {
                        clientCode: client?.ClientCode,
                        action: "Load Sales Attribution Setting",
                    },
                });
            }
            if (!cancelled) setLoading(false);
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [client?.Id, isHeartlandRetail]);

    if (!isHeartlandRetail) return null;

    async function onChange(_e, next) {
        if (!next || next === mode) return;
        const previous = mode;
        setMode(next);
        setSaving(true);
        try {
            await PosService.saveHeartlandSalesAttribution(client.Id, next);
            tip.showSuccess(
                next === "source"
                    ? "Attribution set to source location."
                    : "Attribution set to fulfillment location."
            );
            trackEvent("Client POS Event", {
                action: "Set Heartland Sales Attribution",
                clientCode: client?.ClientCode,
                mode: next,
            });
        } catch (err) {
            setMode(previous);
            tip.show("Could not save setting");
            handleTrackError({
                error: err,
                pageName: "Client POS Mapping",
                additionalMetadata: {
                    clientCode: client?.ClientCode,
                    action: "Save Sales Attribution Setting",
                },
            });
        }
        setSaving(false);
    }

    return (
        <Paper elevation={2} sx={{ mt: 2 }}>
            <SectionTitle type="primary" label="Sales Attribution" />
            <Box sx={{ p: 2 }}>
                {loading || saving ? (
                    <LinearProgress
                        sx={{
                            mb: 1.5,
                            borderRadius: 1,
                            height: 3,
                            backgroundColor: "rgba(92,63,117,0.1)",
                            "& .MuiLinearProgress-bar": { backgroundColor: "primary.main" },
                        }}
                    />
                ) : null}

                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={onChange}
                    size="small"
                    disabled={loading || saving}
                    sx={{
                        "& .MuiToggleButton-root": {
                            textTransform: "none",
                            fontSize: "0.78rem",
                            px: 1.5,
                            py: 0.5,
                        },
                        "& .Mui-selected": {
                            backgroundColor: "rgba(92,63,117,0.12) !important",
                            color: "primary.main",
                            fontWeight: 600,
                        },
                    }}
                >
                    <ToggleButton value="fulfillment">Fulfillment</ToggleButton>
                    <ToggleButton value="source">Source</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Paper>
    );
}

export default HeartlandSalesAttributionToggle;
